import { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ValueGetterParams, CellClickedEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { z } from "zod";
import {
  tableExtractionRow,
  isBaseUnit,
  jsonScalarType,
} from "../../models/extraction";
import { attributesValidator } from "../../models/schemasV2";
import { useTheme } from "../../contexts/themeContext";

interface ExtractionTableProps {
  table: z.infer<typeof tableExtractionRow>[];
  attributes: Record<string, z.infer<typeof attributesValidator>>;
  setSourceElementId?: (sourceElementId: string) => void;
}

const ExtractionTable = ({
  table,
  attributes,
  setSourceElementId,
}: ExtractionTableProps) => {
  const { resolvedTheme } = useTheme();
  const orderedAttributeKeys = useMemo(() => {
    if (table.length === 0) return [];

    const tableKeys = new Set(Object.keys(table[0].data));
    return Object.entries(attributes)
      .filter(([key]) => tableKeys.has(key))
      .sort(([, a], [, b]) => {
        if (a.attributeOrder == null && b.attributeOrder == null) return 0;
        if (a.attributeOrder == null) return -1;
        if (b.attributeOrder == null) return 1;
        return a.attributeOrder - b.attributeOrder;
      })
      .map(([key]) => key);
  }, [table, attributes]);

  const isBase64Image = (value: z.infer<typeof jsonScalarType>): boolean => {
    if (typeof value !== "string") {
      return false;
    }
    return value.startsWith("data:image");
  };

  const columnDefs: ColDef[] = useMemo(() => {
    const cellRenderer = (key: string) => (params: ValueGetterParams) => {
      const value = params.data.data[key];

      if (isBaseUnit(value)) {
        const baseValue = value.value;
        return baseValue;
      }

      if (isBase64Image(value ?? "")) {
        return <img src={value} style={{ maxHeight: "50px" }} alt="image" />;
      }

      return value;
    };

    return orderedAttributeKeys.map((key) => ({
      field: key,
      headerName: attributes[key].name,
      wrapText: true,
      autoHeight: true,
      cellRenderer: cellRenderer(key),
    }));
  }, [orderedAttributeKeys, attributes]);

  const rowData = useMemo(() => {
    return table.map((row) => ({
      ...row,
      id: row.metadata["_source_element_id"] || Math.random().toString(),
    }));
  }, [table]);

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
    };
  }, []);

  const onCellClicked = (event: CellClickedEvent) => {
    if (event.type !== "cellClicked") {
      return;
    }
    const attributeId = event.colDef.field;
    if (!attributeId) {
      return;
    }

    // If the attribute has a cell level source element id, set it
    if (
      setSourceElementId &&
      event.data.metadata["_column_source_element_ids"]
    ) {
      if (event.data.metadata["_column_source_element_ids"][attributeId]) {
        setSourceElementId(
          event.data.metadata["_column_source_element_ids"][attributeId],
        );
        return;
      }
    }

    // else If the row has a source element id, set it
    if (setSourceElementId && event.data.metadata["_source_element_id"]) {
      setSourceElementId(event.data.metadata["_source_element_id"]);
    }
  };

  if (!table) {
    return <div>No extraction data available</div>;
  }

  return (
    <div
      className={
        resolvedTheme === "dark" ? "ag-theme-quartz-dark" : "ag-theme-quartz"
      }
      style={{ width: "100%" }}
    >
      <AgGridReact
        domLayout="autoHeight"
        columnDefs={columnDefs}
        rowData={rowData}
        defaultColDef={defaultColDef}
        onCellClicked={onCellClicked}
      />
    </div>
  );
};

export default ExtractionTable;
